import type { FriendVisitDetails } from '.';
import { Feature } from '../analytics';
import { generateReplicantChatbotPayload } from '../chatbot/chatbotMessage';

/**
 * Sent when the player has arrived at a friend's room
 *
 * @param eventProperties.friendID User ID of the friend player the player is visiting
 * @param eventProperties.friendPetID Pet ID (species) of the friend’s pet
 * @param eventProperties.friendPetStomach Value of the stomach gauge of the friend’s pet
 * @param eventProperties.friendPetLove Value of the love gauge of the friend’s pet
 * @param eventProperties.friendDirtiness Value of the dirtiness of the friend’s pet
 * @param eventProperties.friendUrine Value of the urine of of the friend’s pet
 * @param eventProperties.friendStress Value of the stress of the friend’s pet
 * @param eventProperties.friendToothAcidity Value of the tooth_acidity of the friend’s pet
 * @param eventProperties.friendAge Age of the friend’s pet
 * @param eventProperties.friendPlayerLevel Player level of the friend
 * @param eventProperties.visits Number of times the player has visited the friend including the current visit
 * @param eventProperties.visited Number of times the player has been visited by the friend
 */
export const getFriendVisitStartEvent = (eventProperties: {
  friendID: string;
  friendPetID: string;
  friendPetStomach: number;
  friendPetLove: number;
  friendDirtiness: number;
  friendUrine: number;
  friendStress: number;
  friendToothAcidity: number;
  friendAge: number;
  friendPlayerLevel: number;
  visits: number;
  visited: number;
}) => ({
  eventType: 'FriendVisitStart',
  eventProperties,
});

/**
 * Sent when the player has left a friend's room
 *
 * @param eventProperties.friendID User ID of the friend player the player is visiting
 * @param eventProperties.friendPetID Pet ID (species) of the friend’s pet
 * @param eventProperties.friendPetStomach Value of the stomach gauge of the friend’s pet
 * @param eventProperties.friendPetLove Value of the love gauge of the friend’s pet
 * @param eventProperties.friendDirtiness Value of the dirtiness of the friend’s pet
 * @param eventProperties.friendUrine Value of the urine of of the friend’s pet
 * @param eventProperties.friendStress Value of the stress of the friend’s pet
 * @param eventProperties.friendToothAcidity Value of the tooth_acidity of the friend’s pet
 * @param eventProperties.friendAge Age of the friend’s pet
 * @param eventProperties.friendPlayerLevel Player level of the friend
 * @param eventProperties.visits Number of times the player has visited the friend including the current visit
 * @param eventProperties.visited Number of times the player has been visited by the friend
 * @param eventProperties.careActions Number of times the player has taken care actions during the visit
 * @param eventProperties.friendDialogs Number of times the player has started dialogs with the friend’s player during the visit
 * @param eventProperties.gemsReceived Amount of gems the player received from the friend’s pet during the visit
 * @param eventProperties.coinsSpent Amount of coins (B-points) the player has spent during the visit
 */
export const getFriendVisitFinishEvent = (eventProperties: {
  friendID: string;
  friendPetID: string;
  friendPetStomach: number;
  friendPetLove: number;
  friendDirtiness: number;
  friendUrine: number;
  friendStress: number;
  friendToothAcidity: number;
  friendAge: number;
  friendPlayerLevel: number;
  visits: number;
  visited: number;
  careActions: number;
  friendDialogs: number;
  gemsReceived: number;
  coinsSpent: number;
}) => ({
  eventType: 'FriendVisitFinish',
  eventProperties,
});

export const getHadVisitorChatbotPayload = (args: {
  friendId: string;
  friendPet: FriendVisitDetails;
  visits: number;
  visited: number;
  isFriend: boolean;
}) => ({
  ...generateReplicantChatbotPayload({
    feature: Feature.VISIT._,
    $subFeature: args.isFriend
      ? Feature.VISIT.FRIEND
      : Feature.VISIT.NON_FRIEND,
  }),
  friendID: args.friendId,
  friendPetID: args.friendPet.currentEvolution?.species,
  friendPetStomach: args.friendPet.attributes.stomach,
  friendPetLove: args.friendPet.attributes.love,
  friendDirtiness: args.friendPet.attributes.dirtiness,
  friendUrine: args.friendPet.attributes.urine,
  friendStress: args.friendPet.attributes.stress,
  friendToothAcidity: args.friendPet.attributes.toothAcidity,
  friendAge: args.friendPet.age,
  friendPlayerLevel: args.friendPet.level,
  visits: args.visits,
  visited: args.visited,
});
